import type { FormEvent, ReactNode } from 'react'
import type { FieldErrors, ReservationInput, RestaurantNow } from '@/lib/booking'

import { ArrowRight, ChevronDown } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import Field, { describedBy, inputClass } from '@/components/generals/form/field'
import Stepper from '@/components/generals/form/stepper'
import TransitionLink from '@/components/generals/transition-link'
import { siteConfig } from '@/constants/site'

import {
    MAX_DAYS_AHEAD,
    MAX_PARTY,
    MIN_PARTY,
    addDays,
    formatLongDate,
    restaurantNow,
    timeSlots,
    validateReservation
} from '@/lib/booking'

type Status = 'idle' | 'sending' | 'sent'

const EMPTY: ReservationInput = {
    date: '',
    time: '',
    guests: 2,
    name: '',
    phone: '',
    requests: ''
}

const ORDER: Array<keyof ReservationInput> = ['date', 'time', 'guests', 'name', 'phone', 'requests']

const ReservationForm = (): ReactNode => {
    const [values, setValues] = useState<ReservationInput>(EMPTY)
    const [errors, setErrors] = useState<FieldErrors<ReservationInput>>({})
    const [attempted, setAttempted] = useState(false)
    const [status, setStatus] = useState<Status>('idle')
    const [failure, setFailure] = useState<string | null>(null)
    const [company, setCompany] = useState('')
    const [now, setNow] = useState<RestaurantNow | null>(null)
    const [reference, setReference] = useState('')
    const confirmation = useRef<HTMLDivElement>(null)

    useEffect(() => {
        setNow(restaurantNow())
    }, [])

    const slots = useMemo(() => timeSlots(values.date, now ?? undefined), [values.date, now])

    useEffect(() => {
        if (values.time && !slots.includes(values.time)) {
            setValues(previous => ({ ...previous, time: '' }))
        }
    }, [slots, values.time])

    useEffect(() => {
        if (attempted) setErrors(validateReservation(values, now ?? undefined))
    }, [values, attempted, now])

    useEffect(() => {
        if (status === 'sent') confirmation.current?.focus()
    }, [status])

    const update = <K extends keyof ReservationInput>(key: K, value: ReservationInput[K]) => {
        setValues(previous => ({ ...previous, [key]: value }))
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'sending') return

        const current = restaurantNow()

        setNow(current)
        setAttempted(true)
        setFailure(null)

        const found = validateReservation(values, current)

        setErrors(found)

        const first = ORDER.find(key => found[key])

        if (first) {
            document.getElementById(`reserve-${first}`)?.focus()
            return
        }

        setStatus('sending')

        try {
            const response = await fetch('/api/reservations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, company })
            })

            const payload = await response.json().catch(() => ({})) as {
                errors?: FieldErrors<ReservationInput>
                message?: string
                reference?: string
            }

            if (response.status === 400 && payload.errors) {
                setErrors(payload.errors)
                setStatus('idle')

                const serverFirst = ORDER.find(key => payload.errors?.[key])

                if (serverFirst) document.getElementById(`reserve-${serverFirst}`)?.focus()
                return
            }

            if (!response.ok) throw new Error(payload.message || 'Request failed')

            setReference(payload.reference ?? '')
            setStatus('sent')
        }
        catch {
            setStatus('idle')
            setFailure(`Your request did not reach us. Please try again in a moment, or call us on ${siteConfig.contact.phone}.`)
        }
    }

    const reset = () => {
        setValues(EMPTY)
        setErrors({})
        setAttempted(false)
        setReference('')
        setStatus('idle')
    }

    if (status === 'sent') {
        const firstName = values.name.trim().split(/\s+/)[0]
        const guestsLabel = `${values.guests} ${values.guests === 1 ? 'guest' : 'guests'}`

        return (
            <div ref={confirmation} tabIndex={-1} aria-live='polite' className='pairing-in outline-none'>
                <p className='caps flex items-center gap-4 text-brass'>
                    <span>Request received</span>
                </p>

                <h2 className='mt-8 font-heading text-[clamp(2.6rem,4.6vw,4.25rem)] leading-[0.98] tracking-[-0.03em]'>
                    Thank you, <em>{firstName}.</em>
                </h2>

                <p className='mt-6 text-[1.1rem] leading-relaxed text-ivory/75'>
                    We have your request for a table for {guestsLabel} on {formatLongDate(values.date)} at {values.time}. We will call you on {values.phone.trim()} to confirm.
                </p>

                <dl className='mt-10 grid grid-cols-2 gap-px border border-ivory/10 bg-ivory/10'>
                    {[
                        ['Date', formatLongDate(values.date)],
                        ['Time', values.time],
                        ['Guests', guestsLabel],
                        ['Name', values.name.trim()],
                        ['Phone', values.phone.trim()],
                        ['Reference', reference || '-']
                    ].map(([label, value]) => (
                        <div key={label} className='bg-charcoal p-5'>
                            <dt className='caps text-ivory/45'>{label}</dt>
                            <dd className='mt-2 break-words tabular-nums text-ivory/90'>{value}</dd>
                        </div>
                    ))}
                </dl>

                {values.requests.trim() && (
                    <div className='mt-px border border-t-0 border-ivory/10 bg-charcoal p-5'>
                        <p className='caps text-ivory/45'>Special requests</p>
                        <p className='mt-2 whitespace-pre-line leading-relaxed text-ivory/80'>{values.requests.trim()}</p>
                    </div>
                )}

                <p className='mt-8 text-[0.95rem] leading-relaxed text-ivory/55'>
                    A request, not yet a booking. If your plans change before we call, ring us on{' '}
                    <a href={siteConfig.contact.phoneHref} className='text-brass transition-colors duration-500 hover:text-brass-bright'>
                        {siteConfig.contact.phone}
                    </a>
                    .
                </p>

                <button type='button' onClick={reset} className='link-line caps mt-10 text-ivory/80'>
                    Make another request
                </button>
            </div>
        )
    }

    const minDate = now?.date
    const maxDate = now ? addDays(now.date, MAX_DAYS_AHEAD) : undefined
    const noSlotsToday = Boolean(values.date) && slots.length === 0

    return (
        <form noValidate onSubmit={onSubmit} aria-describedby='reserve-note' className='grid gap-x-8 gap-y-9 sm:grid-cols-2'>
            <p id='reserve-note' className='sr-only'>All fields are required except special requests.</p>

            <div aria-hidden='true' className='absolute -left-[9999px] h-0 w-0 overflow-hidden'>
                <label htmlFor='reserve-company'>Company</label>
                <input
                    id='reserve-company'
                    name='company'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={company}
                    onChange={event => setCompany(event.target.value)}
                />
            </div>

            <Field id='reserve-date' label='Date' error={errors.date}>
                <input
                    id='reserve-date'
                    type='date'
                    min={minDate}
                    max={maxDate}
                    value={values.date}
                    onChange={event => update('date', event.target.value)}
                    aria-invalid={Boolean(errors.date)}
                    aria-describedby={describedBy('reserve-date', undefined, errors.date)}
                    className={cn(inputClass, 'min-h-[3.25rem]')}
                />
            </Field>

            <Field
                id='reserve-time'
                label='Time'
                error={errors.time}
                hint={noSlotsToday ? "The last seating today has passed. Choose another day, or walk in for the counter." : undefined}
            >
                <div className='relative'>
                    <select
                        id='reserve-time'
                        value={values.time}
                        onChange={event => update('time', event.target.value)}
                        aria-invalid={Boolean(errors.time)}
                        aria-describedby={describedBy('reserve-time', noSlotsToday ? 'hint' : undefined, errors.time)}
                        className={cn(inputClass, 'min-h-[3.25rem] appearance-none pr-8 [&_option]:bg-charcoal [&_option]:text-ivory', !values.time && 'text-ivory/45')}
                    >
                        <option value='' disabled>
                            {noSlotsToday ? 'No more tables today' : 'Choose a time'}
                        </option>

                        {slots.map(slot => (
                            <option key={slot} value={slot}>
                                {slot}
                            </option>
                        ))}
                    </select>

                    <ChevronDown aria-hidden='true' className='pointer-events-none absolute right-0 top-1/2 size-4 -translate-y-1/2 text-ivory/50' strokeWidth={1.25} />
                </div>
            </Field>

            <Field
                id='reserve-guests'
                label='Guests'
                error={errors.guests}
                className='sm:col-span-2'
            >
                <Stepper
                    id='reserve-guests'
                    value={values.guests}
                    min={MIN_PARTY}
                    max={MAX_PARTY}
                    onChange={value => update('guests', value)}
                    invalid={Boolean(errors.guests)}
                    describedBy={describedBy('reserve-guests', 'hint', errors.guests)}
                />

                <p id='reserve-guests-hint' className='mt-3 text-sm leading-relaxed text-ivory/45'>
                    Up to twelve at one table. For more,{' '}
                    <TransitionLink href='/private-dining' className='text-brass underline decoration-brass/40 underline-offset-4 transition-colors duration-500 hover:text-brass-bright'>
                        dine privately
                    </TransitionLink>
                    .
                </p>
            </Field>

            <Field id='reserve-name' label='Name' error={errors.name}>
                <input
                    id='reserve-name'
                    type='text'
                    autoComplete='name'
                    value={values.name}
                    onChange={event => update('name', event.target.value)}
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={describedBy('reserve-name', undefined, errors.name)}
                    className={inputClass}
                />
            </Field>

            <Field id='reserve-phone' label='Phone' error={errors.phone}>
                <input
                    id='reserve-phone'
                    type='tel'
                    autoComplete='tel'
                    inputMode='tel'
                    value={values.phone}
                    onChange={event => update('phone', event.target.value)}
                    aria-invalid={Boolean(errors.phone)}
                    aria-describedby={describedBy('reserve-phone', undefined, errors.phone)}
                    className={inputClass}
                />
            </Field>

            <Field
                id='reserve-requests'
                label='Special requests'
                optional
                error={errors.requests}
                className='sm:col-span-2'
            >
                <textarea
                    id='reserve-requests'
                    rows={3}
                    placeholder='Allergies, celebrations, high chairs'
                    value={values.requests}
                    onChange={event => update('requests', event.target.value)}
                    aria-invalid={Boolean(errors.requests)}
                    aria-describedby={describedBy('reserve-requests', undefined, errors.requests)}
                    className={cn(inputClass, 'resize-none leading-relaxed')}
                />
            </Field>

            <div className='flex flex-col gap-5 sm:col-span-2'>
                <button
                    type='submit'
                    disabled={status === 'sending'}
                    className='caps group inline-flex h-14 w-full items-center justify-center gap-4 bg-brass px-9 tracking-[0.26em] text-charcoal transition-colors duration-500 ease-film hover:bg-brass-bright disabled:opacity-60 sm:w-auto sm:self-start'
                >
                    {status === 'sending' ? 'Sending' : 'Request a table'}
                    <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-500 group-hover:translate-x-1' strokeWidth={1.5} />
                </button>

                <p className='text-sm leading-relaxed text-ivory/45'>
                    We confirm every request by phone. Nothing is charged.
                </p>
            </div>

            {failure && (
                <p role='alert' className='border border-destructive/40 bg-destructive/[0.06] p-5 text-[0.95rem] leading-relaxed text-ivory/85 sm:col-span-2'>
                    {failure}
                </p>
            )}

            {attempted && Object.keys(errors).length > 0 && (
                <p role='status' className='sr-only'>
                    Some details need attention. {Object.keys(errors).length} field{Object.keys(errors).length === 1 ? '' : 's'} to check.
                </p>
            )}
        </form>
    )
}

export default ReservationForm
